/**
 * useEntityModal - Global hook for entity modal management
 *
 * Detects entity IDs at the end of ANY URL and provides modal control.
 * This allows opening entities from anywhere in the app.
 *
 * URL Examples:
 * - /today/eml-123          → Opens email modal over Today view
 * - /hub/cnv-abc            → Opens conversation modal over Hub main
 * - /hub/email/INBOX/eml-5  → Opens email modal over INBOX list
 * - /projects/prj-xyz/tsk-1 → Opens task modal over project view
 *
 * Usage:
 * ```tsx
 * function App() {
 *   const { entityId, entityType, isOpen, closeEntity } = useEntityModal();
 *
 *   return (
 *     <>
 *       <Routes>...</Routes>
 *       {isOpen && <EntityModalRenderer type={entityType} id={entityId} onClose={closeEntity} />}
 *     </>
 *   );
 * }
 * ```
 */

import { useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// =============================================================================
// TYPES
// =============================================================================

/** Entity type prefixes - all use hyphen format: prefix-uniqueId */
const ENTITY_PATTERNS: Array<{ prefix: string; type: string }> = [
  { prefix: 'eml-', type: 'eml' },   // emails
  { prefix: 'cnv-', type: 'cnv' },   // conversations
  { prefix: 'cnt-', type: 'cnt' },   // contacts
  { prefix: 'cal-', type: 'cal' },   // calls
  { prefix: 'msg-', type: 'msg' },   // messages
  { prefix: 'prj-', type: 'prj' },   // projects
  { prefix: 'tsk-', type: 'tsk' },   // tasks
  { prefix: 'mls-', type: 'mls' },   // milestones
  { prefix: 'tim-', type: 'tim' },   // time entries
  { prefix: 'evt-', type: 'evt' },   // events
  { prefix: 'inv-', type: 'inv' },   // invoices
  { prefix: 'mov-', type: 'mov' },   // movements
];

export type EntityType = 'eml' | 'cnv' | 'cnt' | 'cal' | 'msg' | 'prj' | 'tsk' | 'mls' | 'tim' | 'evt' | 'inv' | 'mov';

export interface UseEntityModalResult {
  /** The entity ID if present in URL */
  entityId: string | undefined;

  /** The entity type (eml, cnv, cnt, etc.) */
  entityType: EntityType | undefined;

  /** Whether an entity modal is currently open */
  isOpen: boolean;

  /** The base path (URL without the entity ID) */
  basePath: string;

  /** Open an entity modal (appends ID to current URL) */
  openEntity: (entityId: string) => void;

  /** Close the entity modal (removes ID from URL) */
  closeEntity: () => void;

  /** Navigate to a specific path and open an entity */
  navigateAndOpen: (path: string, entityId: string) => void;
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Check if a URL segment is an entity ID
 */
function parseEntityId(segment: string): { id: string; type: EntityType } | null {
  for (const pattern of ENTITY_PATTERNS) {
    if (segment.startsWith(pattern.prefix)) {
      return { id: segment, type: pattern.type as EntityType };
    }
  }
  return null;
}

/**
 * Check if a string is an entity ID
 */
export function isEntityId(str: string): boolean {
  return parseEntityId(str) !== null;
}

/**
 * Get entity type from ID
 */
export function getEntityType(entityId: string): EntityType | undefined {
  const parsed = parseEntityId(entityId);
  return parsed?.type;
}

// =============================================================================
// HOOK
// =============================================================================

export function useEntityModal(): UseEntityModalResult {
  const navigate = useNavigate();
  const location = useLocation();

  // Parse URL to extract entity ID and base path
  const { entityId, entityType, basePath } = useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean);

    if (segments.length === 0) {
      return { entityId: undefined, entityType: undefined, basePath: '/' };
    }

    // Check last segment for entity ID
    const lastSegment = segments[segments.length - 1];
    const parsed = parseEntityId(lastSegment);

    if (parsed) {
      // Last segment is an entity ID
      const baseSegments = segments.slice(0, -1);
      return {
        entityId: parsed.id,
        entityType: parsed.type,
        basePath: '/' + baseSegments.join('/'),
      };
    }

    // No entity ID found
    return {
      entityId: undefined,
      entityType: undefined,
      basePath: location.pathname,
    };
  }, [location.pathname]);

  const isOpen = !!entityId;

  // Open entity by appending ID to current path
  const openEntity = useCallback(
    (newEntityId: string) => {
      // If already viewing an entity, replace it
      const targetPath = isOpen ? basePath : location.pathname;
      // Ensure no double slashes
      const cleanPath = targetPath.endsWith('/') ? targetPath.slice(0, -1) : targetPath;
      navigate(`${cleanPath}/${newEntityId}`);
    },
    [navigate, location.pathname, basePath, isOpen]
  );

  // Close entity by navigating to base path
  const closeEntity = useCallback(() => {
    if (basePath) {
      navigate(basePath || '/');
    }
  }, [navigate, basePath]);

  // Navigate to specific path and open entity
  const navigateAndOpen = useCallback(
    (path: string, newEntityId: string) => {
      const cleanPath = path.endsWith('/') ? path.slice(0, -1) : path;
      navigate(`${cleanPath}/${newEntityId}`);
    },
    [navigate]
  );

  return {
    entityId,
    entityType,
    isOpen,
    basePath,
    openEntity,
    closeEntity,
    navigateAndOpen,
  };
}

export default useEntityModal;
